/**
 * Tutorial Overlay Component
 * 
 * Integrates react-joyride with tutorial step definitions to provide
 * interactive tutorial functionality with navigation controls and progress indicators.
 * 
 * Requirements: 1.4, 1.5, 1.6, 5.2
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import Joyride, { 
  CallBackProps, 
  STATUS, 
  EVENTS, 
  ACTIONS,
  Step as JoyrideStep,
  Styles as JoyrideStyles 
} from 'react-joyride';
import { useTutorial } from './TutorialProvider';
import type { TutorialStep } from '../types/tutorial';
import { getTutorialProgress } from '../utils/tutorialHelpers';
import {
  useResponsiveTutorial, 
  mergeResponsiveStyles,
} from '../utils/responsiveTutorial';
import {
  TutorialAccessibilityManager,
  generateTutorialAccessibilityAttributes,
  generateTutorialProgressAttributes,
  prefersReducedMotion,
  prefersHighContrast,
} from '../utils/tutorialAccessibility';
import './TutorialOverlay.css';

/**
 * Tutorial Overlay Props
 */
interface TutorialOverlayProps {
  /** Whether to show debug information */
  debug?: boolean;
  /** Custom styling overrides */
  styles?: Partial<JoyrideStyles>;
  /** Whether to show progress indicators */
  showProgress?: boolean;
  /** Whether to show skip button */
  showSkipButton?: boolean;
  /** Whether to enable continuous mode */
  continuous?: boolean;
  /** Whether to scroll to first step */
  scrollToFirstStep?: boolean;
}

/**
 * Filter out tutorial steps where the target element doesn't exist in the DOM
 * This allows us to have device-specific steps that are automatically skipped
 * when the target element is not visible (e.g., mobile vs desktop elements)
 */
function filterAvailableSteps(tutorialSteps: TutorialStep[]): TutorialStep[] {
  return tutorialSteps.filter((step) => {
    if (typeof step.target === 'string') {
      const targetElement = document.querySelector(step.target);
      if (!targetElement) {
        console.log(`[TutorialOverlay] Filtering out step "${step.id}" - target not found: ${step.target}`);
        return false;
      }
    }
    return true;
  });
}

/**
 * Convert TutorialStep to Joyride Step format with accessibility enhancements
 * Simplified - let react-joyride handle placement naturally
 */
function convertToJoyrideSteps(tutorialSteps: TutorialStep[], isMobile: boolean = false): JoyrideStep[] {
  return tutorialSteps.map((step, index) => {
    const stepNumber = index + 1;
    const totalSteps = tutorialSteps.length;
    
    // Use original target and placement - no overrides
    const target = step.target;
    const placement = step.placement || 'bottom';
    
    // Validate target exists
    if (typeof target === 'string') {
      const targetElement = document.querySelector(target);
      if (!targetElement) {
        console.warn(`[TutorialOverlay] Target element not found: ${target}`);
      }
    }
    
    return {
      target,
      content: step.content,
      title: step.title,
      placement,
      disableBeacon: step.disableBeacon || false,
      hideCloseButton: false, // Always show close button
      hideFooter: step.hideFooter || false,
      styles: step.styles || {},
      spotlightClicks: false,
      spotlightPadding: isMobile ? 8 : 12,
      // Add accessibility attributes
      ...generateTutorialAccessibilityAttributes(step, stepNumber, totalSteps),
    };
  });
}

/**
 * Tutorial Overlay Component
 * 
 * Provides the main tutorial interface using react-joyride with custom
 * navigation controls, progress indicators, and completion handling.
 * Now includes responsive design support and comprehensive accessibility features.
 */
export const TutorialOverlay: React.FC<TutorialOverlayProps> = ({
  debug = false,
  styles = {},
  showProgress = true,
  showSkipButton = false, // Hide skip button - X button is sufficient
  continuous = true,
  scrollToFirstStep = true,
}) => {
  const {
    state,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
  } = useTutorial();

  const [joyrideKey, setJoyrideKey] = useState(0);
  const tutorialContainerRef = useRef<HTMLDivElement>(null);
  const accessibilityManagerRef = useRef<TutorialAccessibilityManager | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Use responsive tutorial hook for device-aware configuration
  const {
    deviceType,
    isMobile,
    responsiveConfig,
    adaptSteps,
  } = useResponsiveTutorial();

  /**
   * Merge custom styles with responsive styles and accessibility preferences
   */
  const mergedStyles: Partial<JoyrideStyles> = React.useMemo(() => {
    const baseStyles = mergeResponsiveStyles(styles, deviceType);
    
    // Apply accessibility preferences
    if (prefersHighContrast()) {
      baseStyles.options = {
        ...baseStyles.options,
        primaryColor: '#000000',
        textColor: '#000000',
      };
      baseStyles.overlay = {
        ...baseStyles.overlay,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
      };
    }
    
    return baseStyles;
  }, [styles, deviceType]);

  /**
   * Convert and adapt tutorial steps to Joyride format with responsive settings
   * Filter out steps where target elements don't exist (e.g., mobile vs desktop)
   */
  const joyrideSteps = convertToJoyrideSteps(
    filterAvailableSteps(adaptSteps(state.currentSteps)), 
    isMobile
  );

  /**
   * Get responsive tutorial configuration with accessibility adjustments
   */
  const tutorialConfig = React.useMemo(() => {
    const config = {
      ...responsiveConfig,
      continuous,
      showProgress: showProgress && !isMobile, // Hide progress on mobile
      showSkipButton,
      scrollToFirstStep: scrollToFirstStep && !isMobile, // Disable scroll on mobile
    };
    
    // Adjust for reduced motion preference
    if (prefersReducedMotion()) {
      config.scrollDuration = 0;
    }
    
    return config;
  }, [responsiveConfig, continuous, showProgress, isMobile, showSkipButton, scrollToFirstStep]);

  /**
   * Get tutorial progress information
   */
  const progress = getTutorialProgress(state.stepIndex, state.totalSteps);

  /**
   * Initialize accessibility manager
   */
  useEffect(() => {
    if (state.isRunning && tutorialContainerRef.current) {
      if (!accessibilityManagerRef.current) {
        accessibilityManagerRef.current = new TutorialAccessibilityManager();
      }
      
      accessibilityManagerRef.current.initialize(tutorialContainerRef.current, {
        onNext: nextStep,
        onPrevious: previousStep,
        onSkip: skipTutorial,
        onClose: skipTutorial,
      });
    }
    
    return () => {
      if (accessibilityManagerRef.current) {
        accessibilityManagerRef.current.cleanup();
      }
    };
  }, [state.isRunning, nextStep, previousStep, skipTutorial]);

  /**
   * Handle accessibility announcements for step changes
   */
  useEffect(() => {
    if (state.isRunning && accessibilityManagerRef.current && state.currentSteps.length > 0) {
      const currentStep = state.currentSteps[state.stepIndex];
      if (currentStep) {
        accessibilityManagerRef.current.handleStepChange(
          progress.current,
          progress.total,
          String(currentStep.title || `Step ${progress.current}`)
        );
      }
    }
  }, [state.stepIndex, state.isRunning, progress.current, progress.total, state.currentSteps]);

  /**
   * Handle Joyride callback events with enhanced error handling and accessibility
   */
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { action, index, status, type, step } = data;

    // Always log for debugging
    console.log('[TutorialOverlay] Joyride callback:', { action, index, status, type, lifecycle: data.lifecycle });

    if (debug) {
      console.log('[TutorialOverlay] Full callback data:', data);
    }

    // Handle target not found errors gracefully
    if (type === EVENTS.TARGET_NOT_FOUND) {
      console.warn(`[TutorialOverlay] Target not found for step ${index}:`, step?.target);
      
      // Skip to next available step
      const nextIndex = index + 1;
      if (nextIndex < state.totalSteps) {
        console.log(`[TutorialOverlay] Skipping to step ${nextIndex} due to missing target`);
        nextStep();
      } else {
        console.log('[TutorialOverlay] No more steps available, completing tutorial');
        completeTutorial();
      }
      return;
    }

    // Handle step navigation with error recovery
    if (type === EVENTS.STEP_AFTER) {
      try {
        if (action === ACTIONS.NEXT) {
          nextStep();
        } else if (action === ACTIONS.PREV) {
          previousStep();
        } else if (action === ACTIONS.CLOSE) {
          // User clicked the X button
          console.log('[TutorialOverlay] User clicked X button (CLOSE action) - completing tutorial');
          completeTutorial();
        }
      } catch (error) {
        console.error('[TutorialOverlay] Error during step navigation:', error);
        // Continue with tutorial despite navigation error
      }
    } else if (type === EVENTS.TOUR_END) {
      // Tour completion or cancellation with accessibility announcements
      try {
        console.log('[TutorialOverlay] TOUR_END event:', { status, action, index });
        
        if (status === STATUS.FINISHED) {
          // User completed the tutorial by reaching the end
          console.log('[TutorialOverlay] Tutorial FINISHED (reached last step) - triggering completion');
          accessibilityManagerRef.current?.handleTutorialComplete();
          
          // Let TutorialProvider handle the refresh after successful completion
          // Call completeTutorial without chaining since it handles its own success/error flow
          try {
            completeTutorial();
            console.log('[TutorialOverlay] Tutorial completion initiated successfully');
          } catch (error) {
            console.error('[TutorialOverlay] Error initiating tutorial completion:', error);
            // Fallback: force refresh if completion fails to initiate
            setTimeout(() => {
              if (window.location.pathname === '/link-thrift') return;
              console.log('[TutorialOverlay] Forcing page refresh after completion error');
              const pending = sessionStorage.getItem('pendingLinkRedirect');
              if (pending) localStorage.setItem('pendingLinkRedirect', pending);
              window.location.reload();
            }, 1000);
          }
        } else if (status === STATUS.SKIPPED) {
          // User explicitly skipped the tutorial (clicked Skip button)
          console.log('[TutorialOverlay] Tutorial SKIPPED (clicked Skip Tutorial button) - marking as complete');
          accessibilityManagerRef.current?.handleTutorialSkip();
          
          // Mark as completed when skipped
          completeTutorial();
        } else {
          // User closed/cancelled the tutorial (clicked X button or ESC)
          console.log('[TutorialOverlay] Tutorial closed/cancelled (X button or ESC) - marking as complete');
          accessibilityManagerRef.current?.handleTutorialSkip();
          
          // Mark as completed when closed
          completeTutorial();
        }
      } catch (error) {
        console.error('[TutorialOverlay] Error during tutorial completion:', error);
        // Fallback to complete to avoid blocking user
        completeTutorial();
      }
    } else if (type === EVENTS.TOUR_START) {
      if (debug) {
        console.log('[TutorialOverlay] Tutorial started');
      }
    } else if (type === EVENTS.STEP_BEFORE) {
      // Step is about to be shown - validate target exists
      if (debug) {
        console.log(`[TutorialOverlay] About to show step ${index}`);
      }
      
      // Check if target element exists before showing step
      if (step?.target && typeof step.target === 'string') {
        try {
          const element = document.querySelector(step.target);
          if (!element) {
            console.warn(`[TutorialOverlay] Target element not found: ${step.target}`);
            // Let Joyride handle this with TARGET_NOT_FOUND event
          }
        } catch (error) {
          console.warn(`[TutorialOverlay] Invalid target selector: ${step.target}`, error);
        }
      }
    } else if (type === EVENTS.ERROR) {
      // Handle Joyride internal errors
      console.error('[TutorialOverlay] Joyride error:', data);
      
      // Try to recover by skipping to next step or completing tutorial
      if (index < state.totalSteps - 1) {
        console.log('[TutorialOverlay] Attempting to recover by skipping to next step');
        nextStep();
      } else {
        console.log('[TutorialOverlay] Completing tutorial due to error');
        completeTutorial();
      }
    }
  }, [nextStep, previousStep, skipTutorial, completeTutorial, debug, state.totalSteps]);

  /**
   * Reset Joyride when tutorial restarts
   */
  useEffect(() => {
    if (state.isRunning && state.stepIndex === 0) {
      setJoyrideKey(prev => prev + 1);
    }
  }, [state.isRunning, state.stepIndex]);

  /**
   * Detect mobile menu state and pause tutorial when menu is open
   */
  useEffect(() => {
    const checkMobileMenuState = () => {
      const mobileMenuOpen = document.querySelector('.nav-links-open') !== null;
      setIsMobileMenuOpen(mobileMenuOpen);
    };

    // Check immediately
    checkMobileMenuState();

    // Set up observer to watch for mobile menu changes
    const observer = new MutationObserver(checkMobileMenuState);
    
    // Watch for class changes on the document body and nav elements
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  /**
   * Don't render if tutorial is not running or no steps available
   * Also don't render when mobile menu is open to prevent conflicts
   */
  if (!state.isRunning || joyrideSteps.length === 0) {
    console.log('[TutorialOverlay] Not rendering:', { 
      isRunning: state.isRunning, 
      stepsLength: joyrideSteps.length,
      state 
    });
    return null;
  }

  // Don't render tutorial when mobile menu is open
  if (isMobileMenuOpen) {
    console.log('[TutorialOverlay] Mobile menu is open - pausing tutorial');
    return null;
  }

  console.log('[TutorialOverlay] Rendering Joyride:', {
    isRunning: state.isRunning,
    stepIndex: state.stepIndex,
    stepsLength: joyrideSteps.length,
    joyrideSteps: joyrideSteps.map(s => ({ target: s.target, title: s.title })),
    mergedStyles,
    isMobile,
    isMobileMenuOpen
  });

  return (
    <div ref={tutorialContainerRef}>
      <Joyride
        key={joyrideKey}
        steps={joyrideSteps}
        stepIndex={state.stepIndex}
        run={state.isRunning}
        continuous={tutorialConfig.continuous}
        showProgress={tutorialConfig.showProgress}
        showSkipButton={tutorialConfig.showSkipButton}
        scrollToFirstStep={tutorialConfig.scrollToFirstStep}
        disableOverlayClose={tutorialConfig.disableOverlayClose}
        disableScrollParentFix={tutorialConfig.disableScrollParentFix}
        spotlightClicks={tutorialConfig.spotlightClicks}
        spotlightPadding={tutorialConfig.spotlightPadding}
        scrollDuration={tutorialConfig.scrollDuration}
        styles={mergedStyles}
        locale={tutorialConfig.locale}
        floaterProps={tutorialConfig.floaterProps}
        callback={handleJoyrideCallback}
        debug={debug}
      />
      
      {/* Progress Indicator Overlay - Responsive positioning with accessibility */}
      {tutorialConfig.showProgress && state.isRunning && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? 10 : 20,
            right: isMobile ? 10 : 20,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: isMobile ? '6px 10px' : '8px 12px',
            borderRadius: isMobile ? 6 : 4,
            fontSize: isMobile ? 11 : 12,
            zIndex: 10001,
            fontFamily: 'system-ui, -apple-system, sans-serif',
            maxWidth: isMobile ? '120px' : 'auto',
            textAlign: 'center',
          }}
          {...generateTutorialProgressAttributes(progress.current, progress.total)}
        >
          {isMobile ? (
            `${progress.current}/${progress.total}`
          ) : (
            `Step ${progress.current} of ${progress.total} (${progress.percentage}%)`
          )}
        </div>
      )}

      {/* Screen reader only content for tutorial context */}
      <div
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        {state.isRunning && state.currentSteps[state.stepIndex] && (
          <span>
            Tutorial active: {state.currentSteps[state.stepIndex].title || `Step ${progress.current}`}
          </span>
        )}
      </div>

      {/* Debug Information - Responsive positioning and sizing with accessibility */}
      {debug && state.isRunning && (
        <div
          style={{
            position: 'fixed',
            bottom: isMobile ? 10 : 20,
            left: isMobile ? 10 : 20,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: isMobile ? '8px' : '12px',
            borderRadius: 4,
            fontSize: isMobile ? 10 : 11,
            zIndex: 10001,
            fontFamily: 'monospace',
            maxWidth: isMobile ? 250 : 300,
            overflow: 'hidden',
          }}
          role="log"
          aria-label="Tutorial debug information"
        >
          <div><strong>Tutorial Debug Info:</strong></div>
          <div>Device: {deviceType}</div>
          <div>Section: {state.currentSection}</div>
          <div>Step Index: {state.stepIndex}</div>
          <div>Total Steps: {state.totalSteps}</div>
          <div>Is Running: {state.isRunning.toString()}</div>
          <div>Is Completed: {state.isCompleted.toString()}</div>
          <div>Reduced Motion: {prefersReducedMotion().toString()}</div>
          <div>High Contrast: {prefersHighContrast().toString()}</div>
          {state.error && <div style={{ color: '#ff6b6b' }}>Error: {state.error}</div>}
        </div>
      )}
    </div>
  );
};

/**
 * Hook for tutorial overlay configuration
 * 
 * Provides a convenient way to configure tutorial overlay settings
 * with sensible defaults and validation.
 */
export const useTutorialOverlayConfig = (
  overrides: Partial<TutorialOverlayProps> = {}
): TutorialOverlayProps => {
  return {
    debug: false,
    showProgress: true,
    showSkipButton: false, // Hide skip button - X button is sufficient
    continuous: true,
    scrollToFirstStep: true,
    styles: {},
    ...overrides,
  };
};

/**
 * Tutorial Overlay with default configuration
 * 
 * Pre-configured tutorial overlay component with sensible defaults
 * for most use cases.
 */
export const DefaultTutorialOverlay: React.FC = () => {
  const config = useTutorialOverlayConfig();
  return <TutorialOverlay {...config} />;
};

export default TutorialOverlay;